import type {
  PatchAdminV1OrgStoresIdBody,
  PostAdminV1OrgStoresBody,
  Store,
} from '@nucleus-commerce/api-client/admin';
import { money, type Money } from '@nucleus-commerce/core';

import type {
  CreateStoreOutput,
  StoreSettingsInput,
  StoreSettingsOutput,
} from '../schemas/store-form-schema';

const orNull = (v: string): string | null => (v === '' ? null : v);
const minor = (m: Money | null): number | null => (m ? m.amount : null);

/** An address with nothing typed in is cleared on the server, not stored as empty strings. */
function toAddress(values: StoreSettingsOutput['address']): Store['address'] {
  const filled = [values.line1, values.line2, values.city, values.region, values.postalCode].some(
    (s) => s !== '',
  );
  if (!filled) return null;
  return {
    line1: values.line1,
    line2: orNull(values.line2),
    city: values.city,
    region: orNull(values.region),
    postalCode: orNull(values.postalCode),
    countryCode: values.countryCode,
  };
}

export function toStoreCreate(values: CreateStoreOutput): PostAdminV1OrgStoresBody {
  return {
    name: values.name,
    slug: values.slug,
    defaultCurrency: values.defaultCurrency,
    countryCode: values.countryCode,
    timezone: values.timezone,
    defaultLocale: values.defaultLocale,
  };
}

/** Every editable field is sent; the backend merges settings group by group. */
export function toStoreUpdate(values: StoreSettingsOutput): PatchAdminV1OrgStoresIdBody {
  return {
    name: values.name,
    slug: values.slug,
    legalName: orNull(values.legalName),
    contactEmail: orNull(values.contactEmail),
    supportEmail: orNull(values.supportEmail),
    supportPhone: orNull(values.supportPhone),
    primaryDomain: orNull(values.primaryDomain),
    orderNumberPrefix: values.orderNumberPrefix,
    orderNumberPadding: values.orderNumberPadding,
    // Only a change is sent: orders placed meanwhile move the counter and a stale value would 409.
    ...(values.nextOrderNumber !== values.currentNextOrderNumber
      ? { nextOrderNumber: values.nextOrderNumber }
      : {}),
    defaultCurrency: values.defaultCurrency,
    supportedCurrencies: values.supportedCurrencies,
    defaultLocale: values.defaultLocale,
    supportedLocales: values.supportedLocales,
    timezone: values.timezone,
    weightUnit: values.weightUnit,
    countryCode: values.countryCode,
    address: toAddress(values.address),
    settings: {
      checkout: {
        guestCheckout: values.checkout.guestCheckout,
        requirePhone: values.checkout.requirePhone,
        termsUrl: orNull(values.checkout.termsUrl),
        requireTermsAcceptance: values.checkout.requireTermsAcceptance,
        customerLinkUrlTemplate: orNull(values.checkout.customerLinkUrlTemplate),
        codEnabled: values.checkout.codEnabled,
        codMaxAmount: minor(values.checkout.codMaxAmount),
        codFeeAmount: values.checkout.codFeeAmount?.amount ?? 0,
      },
      tracking: {
        ga4Id: orNull(values.tracking.ga4Id),
        metaPixelId: orNull(values.tracking.metaPixelId),
        tiktokPixelId: orNull(values.tracking.tiktokPixelId),
        pinterestTagId: orNull(values.tracking.pinterestTagId),
        googleAdsId: orNull(values.tracking.googleAdsId),
      },
      // indexNowKey is left out on purpose: only POST /seo/indexnow-key sets it.
      seo: {
        titleTemplate: values.seo.titleTemplate,
        defaultMetaDescription: orNull(values.seo.defaultMetaDescription),
        robots: orNull(values.seo.robots),
        indexable: values.seo.indexable,
        organizationLegalName: orNull(values.seo.organizationLegalName),
        socialProfiles: values.seo.socialProfiles,
        twitterHandle: orNull(values.seo.twitterHandle),
        logoMediaId: values.seo.logoMediaId,
      },
      crawlers: { allowTraining: values.crawlers.allowTraining },
      documents: {
        footer: orNull(values.documents.footer.trim()),
        showPrices: values.documents.showPrices,
      },
    },
  };
}

export function fromStore(store: Store): StoreSettingsInput {
  const { checkout, tracking, seo } = store.settings;
  return {
    name: store.name,
    slug: store.slug,
    legalName: store.legalName ?? '',
    contactEmail: store.contactEmail ?? '',
    supportEmail: store.supportEmail ?? '',
    supportPhone: store.supportPhone ?? '',
    primaryDomain: store.primaryDomain ?? '',
    orderNumberPrefix: store.orderNumberPrefix,
    orderNumberPadding: store.orderNumberPadding,
    nextOrderNumber: store.nextOrderNumber,
    currentNextOrderNumber: store.nextOrderNumber,
    defaultCurrency: store.defaultCurrency,
    supportedCurrencies: store.supportedCurrencies,
    defaultLocale: store.defaultLocale,
    supportedLocales: store.supportedLocales,
    timezone: store.timezone,
    weightUnit: store.weightUnit === 'ct' || store.weightUnit === 'oz' ? store.weightUnit : 'g',
    countryCode: store.countryCode,
    address: {
      line1: store.address?.line1 ?? '',
      line2: store.address?.line2 ?? '',
      city: store.address?.city ?? '',
      region: store.address?.region ?? '',
      postalCode: store.address?.postalCode ?? '',
      countryCode: store.address?.countryCode ?? store.countryCode,
    },
    checkout: {
      guestCheckout: checkout.guestCheckout,
      requirePhone: checkout.requirePhone,
      termsUrl: checkout.termsUrl ?? '',
      requireTermsAcceptance: checkout.requireTermsAcceptance,
      customerLinkUrlTemplate: checkout.customerLinkUrlTemplate ?? '',
      codEnabled: checkout.codEnabled,
      codMaxAmount:
        checkout.codMaxAmount === null ? null : money(checkout.codMaxAmount, store.defaultCurrency),
      codFeeAmount: money(checkout.codFeeAmount, store.defaultCurrency),
    },
    tracking: {
      ga4Id: tracking.ga4Id ?? '',
      metaPixelId: tracking.metaPixelId ?? '',
      tiktokPixelId: tracking.tiktokPixelId ?? '',
      pinterestTagId: tracking.pinterestTagId ?? '',
      googleAdsId: tracking.googleAdsId ?? '',
    },
    seo: {
      titleTemplate: seo.titleTemplate,
      defaultMetaDescription: seo.defaultMetaDescription ?? '',
      robots: seo.robots ?? '',
      indexable: seo.indexable,
      organizationLegalName: seo.organizationLegalName ?? '',
      socialProfiles: seo.socialProfiles,
      twitterHandle: seo.twitterHandle ?? '',
      logoMediaId: seo.logoMediaId,
    },
    crawlers: { allowTraining: store.settings.crawlers.allowTraining },
    documents: {
      footer: store.settings.documents.footer ?? '',
      showPrices: store.settings.documents.showPrices,
    },
  };
}

/**
 * The form keeps the settings groups at the top level, so `settings.seo.socialProfiles.2` from
 * the backend lands on `seo.socialProfiles`.
 */
export function settingsField(apiField: string): string {
  const path = apiField.startsWith('settings.') ? apiField.slice('settings.'.length) : apiField;
  return path.replace(/^(seo\.socialProfiles|supported(?:Currencies|Locales))\.\d+$/, '$1');
}

/**
 * The backend refuses a default currency the store does not list as supported and reinterprets
 * every stored minor-unit amount in the new currency, so the form confirms before sending one.
 */
export function changesDefaultCurrency(
  store: Pick<Store, 'defaultCurrency'>,
  values: Pick<StoreSettingsOutput, 'defaultCurrency'>,
): boolean {
  return values.defaultCurrency !== store.defaultCurrency;
}

/** Status moves the backend allows from the current one (draft → active, active ↔ paused, any → closed). */
export function nextStatuses(status: Store['status']): Array<'active' | 'paused' | 'closed'> {
  switch (status) {
    case 'draft':
      return ['active', 'closed'];
    case 'active':
      return ['paused', 'closed'];
    case 'paused':
      return ['active', 'closed'];
    case 'closed':
      return [];
  }
}
